// src/core/ticker.js

export function MotionFlowTicker(userOptions = {}) {
  // -----------------------------------------------------
  // Reduced motion: no-op API
  // -----------------------------------------------------
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    return { refresh() {}, destroy() {}, pause() {}, play() {}, toggle() {} };
  }

  /* =====================================================
     DEFAULTS
  ====================================================== */
  const defaults = {
    speed: 80,
    direction: "left",
    pause: false,
    pauseOnHover: false,
    pauseOnVisibilityChange: true
  };

  // -----------------------------------------------------
  // ✅ Idempotent singleton (so calling MotionFlow.init()
  // multiple times won’t break ticker)
  // -----------------------------------------------------
  const SINGLETON_KEY = "__MF_TICKER_SINGLETON__";
  const existing = window[SINGLETON_KEY];

  // If a ticker singleton already exists, just update options + rebuild safely
  if (existing && existing.__isTickerSingleton) {
    existing.updateOptions(userOptions);
    // Important: re-scan DOM in case init happened in document.ready
    existing.refresh();
    return existing.api;
  }

  /* =====================================================
     INTERNAL STATE
  ====================================================== */
  let instances = [];

  // global config (can be updated)
  let globalCfg = {
    ...defaults,
    ...(userOptions.ticker || {})
  };

  /* =====================================================
     ONE-TIME REFRESH GUARDS
  ====================================================== */
  let fontRefreshDone = false;
  const imageRefreshDone = new WeakSet();

  const dpr = window.devicePixelRatio || 1;
  const MAX_FILL_CYCLES = 60;

  const isHorizontal = d => d === "left" || d === "right";
  const isPositive = d => d === "right" || d === "down";
  const clampDt = dt => Math.min(dt, 0.05);

  function normalizeDirection(dir, fallback) {
    const d = String(dir || "").toLowerCase();
    return ["left", "right", "up", "down"].includes(d) ? d : fallback;
  }

  /* =====================================================
     INTERNAL REBUILD (SAFE + IDEMPOTENT)
  ====================================================== */
  function rebuildTickers() {
    const pausedMap = new Map();

    instances.forEach(inst => {
      pausedMap.set(inst.container, inst.isPaused());
      inst.cancel();
      // Allow re-setup
      inst.container.__mfTickerInited = false;
    });

    instances = [];

    document.querySelectorAll("[data-mf-ticker]").forEach(setupTicker);

    // Restore paused state
    instances.forEach(inst => {
      if (pausedMap.get(inst.container)) {
        inst.setPaused(true);
      }
    });
  }

  /* =====================================================
     FONT READY REFRESH (ONCE)
  ====================================================== */
  function scheduleFontRefresh() {
    if (fontRefreshDone || !document.fonts || !document.fonts.ready) return;

    document.fonts.ready.then(() => {
      if (fontRefreshDone) return;
      fontRefreshDone = true;
      rebuildTickers();
    });
  }

  /* =====================================================
     IMAGE READY REFRESH (PER TICKER)
  ====================================================== */
  function scheduleImageRefresh(container) {
    if (imageRefreshDone.has(container)) return;

    const images = Array.from(container.querySelectorAll("img"));
    if (!images.length) return;

    Promise.all(
      images.map(img =>
        img.complete
          ? Promise.resolve()
          : new Promise(resolve => {
              img.addEventListener("load", resolve, { once: true });
              img.addEventListener("error", resolve, { once: true });
            })
      )
    ).then(() => {
      imageRefreshDone.add(container);
      rebuildTickers();
    });
  }

  /* =====================================================
     ✅ Mutation Observer (optional, but fixes dynamic show/hide)
     - Debounced so it won’t spam rebuild
  ====================================================== */
  let mo = null;
  let moTimer = null;

  function startObserver() {
    if (mo) return;

    const schedule = () => {
      clearTimeout(moTimer);
      moTimer = setTimeout(() => {
        // Re-scan tickers after DOM changes
        rebuildTickers();
      }, 80);
    };

    mo = new MutationObserver(mutations => {
      // Only react when ticker-related nodes/attrs are involved
      for (const m of mutations) {
        if (m.type === "attributes") {
          if (m.attributeName && m.attributeName.startsWith("data-mf-ticker")) {
            schedule();
            return;
          }
        }
        if (m.type === "childList") {
          // If any added/removed nodes contain tickers
          const nodes = [...m.addedNodes, ...m.removedNodes];
          for (const n of nodes) {
            if (n && n.nodeType === 1) {
              if (
                n.matches?.("[data-mf-ticker]") ||
                n.querySelector?.("[data-mf-ticker]")
              ) {
                schedule();
                return;
              }
            }
          }
        }
      }
    });

    mo.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true
      // Note: attributeFilter not used so direction/speed changes also trigger
    });
  }

  function stopObserver() {
    clearTimeout(moTimer);
    moTimer = null;
    mo?.disconnect();
    mo = null;
  }

  /* =====================================================
     INIT (IDEMPOTENT)
  ====================================================== */
  function init() {
    // Safe to call multiple times
    rebuildTickers();
    scheduleFontRefresh();
    startObserver();
  }

  /* =====================================================
     SETUP SINGLE TICKER
  ====================================================== */
  function setupTicker(container) {
    if (container.__mfTickerInited) return;

    // Mark as "in progress" (prevents double-run)
    container.__mfTickerInited = true;

    // Helper: if we can't build (hidden / 0 width), allow retry later
    const fail = () => {
      container.__mfTickerInited = false;
    };

    if (!container.__mfTickerOriginalNodes) {
      container.__mfTickerOriginalNodes = Array.from(container.children);
    }

    const originalNodes = container.__mfTickerOriginalNodes;
    if (!originalNodes.length) {
      fail();
      return;
    }

    scheduleImageRefresh(container);

    const direction = normalizeDirection(
      container.dataset.mfTickerDirection || globalCfg.direction,
      defaults.direction
    );

    let pauseOnVisibilityChange = globalCfg.pauseOnVisibilityChange;
    if (container.dataset.mfTickerPauseOnVisibility === "false") pauseOnVisibilityChange = false;
    if (container.dataset.mfTickerPauseOnVisibility === "true") pauseOnVisibilityChange = true;

    const cfg = {
      speed: Number(container.dataset.mfTickerSpeed) || globalCfg.speed,
      direction,
      pause: globalCfg.pause === true,
      pauseOnHover:
        globalCfg.pause !== true &&
        (
          container.dataset.mfTickerPauseOnHover === "true" ||
          globalCfg.pauseOnHover === true
        ),
      pauseOnVisibilityChange
    };

    const horizontal = isHorizontal(cfg.direction);
    const positiveDir = isPositive(cfg.direction);

    /* -----------------------------
       STRUCTURE
    ----------------------------- */
    const viewport = document.createElement("div");
    const track = document.createElement("div");

    viewport.className = "mf-ticker-viewport";
    track.className = "mf-ticker-track";

    container.innerHTML = "";
    container.appendChild(viewport);
    viewport.appendChild(track);

 
    if (!horizontal) track.style.flexDirection = "column";

    const content = document.createElement("div");
    content.className = "mf-ticker-content"; 
    if (!horizontal) content.style.flexDirection = "column";

    originalNodes.forEach(n => content.appendChild(n));
    track.appendChild(content);

    const measureContent = () => {
      const r = content.getBoundingClientRect();
      return horizontal ? r.width : r.height;
    };

    // ✅ IMPORTANT: if hidden (accordion closed), width/height can be 0
    let baseUnit = measureContent();
    if (!baseUnit) {
      // Allow a retry on next refresh (or after it becomes visible)
      fail();
      return;
    }

    const viewportSize =
      viewport.getBoundingClientRect()[horizontal ? "width" : "height"];

    // If viewport itself is 0 (still hidden), also allow retry
    if (!viewportSize) {
      fail();
      return;
    }

    let filledSize = baseUnit;
    let cycles = 0;

    while (filledSize < viewportSize + baseUnit && cycles < MAX_FILL_CYCLES) {
      originalNodes.forEach(n => content.appendChild(n.cloneNode(true)));
      filledSize = measureContent();
      cycles++;
    }

    const loopSize = measureContent();
    track.appendChild(content.cloneNode(true));

    let pos = positiveDir ? -loopSize : 0;

    /* -----------------------------
       PAUSE STATES
    ----------------------------- */
    let pausedByUser = cfg.pause;
    let pausedByHover = false;
    let pausedByVisibility = false;

    const isPaused = () =>
      pausedByUser || pausedByHover || pausedByVisibility;

    function updatePausedClass() {
      container.classList.toggle("mf-ticker-paused", isPaused());
    }

    let lastT = performance.now();
    let rafId = null;

    function applyTransform(x) {
      const snapped = Math.round(x * dpr) / dpr;
      track.style.transform = horizontal
        ? `translate3d(${snapped}px,0,0)`
        : `translate3d(0,${snapped}px,0)`;
    }

    applyTransform(pos);
    updatePausedClass();

    /* -----------------------------
       HOVER PAUSE
    ----------------------------- */
    const CAN_HOVER = window.matchMedia("(hover: hover)").matches;

    const onEnter = () => {
      if (!pausedByUser && !pausedByVisibility) {
        pausedByHover = true;
        updatePausedClass();
      }
    };

    const onLeave = () => {
      pausedByHover = false;
      updatePausedClass();
    };

    if (cfg.pauseOnHover && CAN_HOVER) {
      viewport.addEventListener("mouseenter", onEnter);
      viewport.addEventListener("mouseleave", onLeave);
    }

    /* -----------------------------
       VISIBILITY PAUSE
    ----------------------------- */
    function onVisibilityChange() {
      pausedByVisibility = document.hidden === true;
      lastT = performance.now();
      updatePausedClass();
    }

    if (cfg.pauseOnVisibilityChange) {
      document.addEventListener("visibilitychange", onVisibilityChange);
    }

    /* -----------------------------
       RAF LOOP
    ----------------------------- */
    function animate(now) {
      const dt = clampDt((now - lastT) / 1000);
      lastT = now;

      if (!isPaused()) {
        const delta = cfg.speed * dt;
        pos += positiveDir ? delta : -delta;

        if (!positiveDir && pos <= -loopSize) pos += loopSize;
        if (positiveDir && pos >= 0) pos -= loopSize;

        applyTransform(pos);
      }

      rafId = requestAnimationFrame(animate);
    }

    rafId = requestAnimationFrame(animate);

    instances.push({
      container,
      setPaused(v) {
        pausedByUser = v;
        updatePausedClass();
      },
      isPaused,
      cancel() {
        cancelAnimationFrame(rafId);
        if (cfg.pauseOnVisibilityChange) {
          document.removeEventListener("visibilitychange", onVisibilityChange);
        }
      }
    });
  }


  /* =====================================================
     INSTANCE SELECTOR
  ====================================================== */
  function getInstances(target) {
    if (!target) return instances;
    const nodes = Array.from(document.querySelectorAll(target));
    return instances.filter(inst => nodes.includes(inst.container));
  }

  /* =====================================================
     PUBLIC API
  ====================================================== */
  function pause(target = null) {
    getInstances(target).forEach(inst => inst.setPaused(true));
  }

  function play(target = null) {
    getInstances(target).forEach(inst => inst.setPaused(false));
  }

  function toggle(target = null) {
    getInstances(target).forEach(inst => inst.setPaused(!inst.isPaused()));
  }

  function refresh() {
    rebuildTickers();
  }

  function destroy() {
    stopObserver();
    instances.forEach(inst => inst.cancel());
    instances = [];

    // allow future init without stale flags
    document.querySelectorAll("[data-mf-ticker]").forEach(el => {
      el.__mfTickerInited = false;
    });

    // reset guards so a fresh init behaves correctly
    fontRefreshDone = false;

    // remove singleton so a future call can recreate fully
    try {
      delete window[SINGLETON_KEY];
    } catch (_) {
      window[SINGLETON_KEY] = null;
    }
  }

  // ✅ allow updating options when MotionFlowTicker is called again
  function updateOptions(nextUserOptions = {}) {
    globalCfg = {
      ...defaults,
      ...(nextUserOptions.ticker || {})
    };
  }

  const api = { refresh, destroy, pause, play, toggle };

  // store singleton
  window[SINGLETON_KEY] = {
    __isTickerSingleton: true,
    api,
    refresh,
    updateOptions
  };

  // run once (safe)
  init();

  return api;
}
